// The queue's SLA column, as a pure function of one list row.
//
// The browser does NO time arithmetic here. `sla_state` arrives already decided by
// the database server, on the same statement and the same clock that decided
// `sla_breached` (src/services/tickets.js, LIST_COLUMNS), so this module is a
// lookup: state in, {label, className, title} out. That is deliberate. The queue is
// a strict one-shot fetch — the only data effect in List.jsx has deps
// `[query, reloadToken]`, and there is no interval, no visibilitychange and no
// focus refetch anywhere in it — so a rendered value freezes at fetch time. A
// ticking client-side countdown would therefore read "12m left" forty minutes
// later, in the same colour, on the wrong side of the deadline: the one transition
// the column exists to announce is the one it could not show. A coarse bucket is
// wrong only for rows within one dwell time of a boundary, instead of wrong for
// every row all the time.
//
// It lives outside List.jsx because there is no DOM test harness in this repo. A
// pure module is the part that CAN be tested with `node --test`
// (test/web/sla-state.test.js), and the mapping is where the real invariant lives:
// it must be TOTAL over the six values the server can send, and it must never
// render the string "undefined" at an agent when it is not.

import { STATUS_LABELS, formatDateTime } from './format.js';

/**
 * Every value `sla_state` can take, in the same order as the CASE branches that
 * produce them. Exported so a test can assert this map is total over it rather
 * than over whatever keys happen to be typed below.
 */
export const SLA_STATES = ['none', 'met', 'closed', 'overdue', 'due_soon', 'on_track'];

// The pair the ORGANIZATION and UPDATED cells already use. text-ink-muted is 4.76:1
// on bg-surface but only 4.34:1 on bg-sunken in LIGHT mode — and bg-sunken is the
// row's own hover fill — so a muted cell drops just under AA exactly while the
// pointer is on it. Promoting to ink-secondary on hover (6.92:1) fixes that with
// the existing vocabulary instead of a private grey.
const MUTED = 'text-ink-muted group-hover:text-ink-secondary';

/**
 * What the SLA cell renders for one list row.
 *
 * @param {{sla_state?: string, sla_first_response_due_at?: string|null,
 *          sla_first_response_satisfied_at?: string|null, status?: string}} ticket
 * @returns {{label: string|null, srLabel: string|null, className: string,
 *           title: string, badge: boolean}}
 *   `label: null` means render an em-dash instead, in which case `srLabel` carries
 *   the meaning for a screen reader — "—" announces as nothing useful, and colour
 *   and a `title` are both non-carriers (a title is not keyboard reachable, is
 *   unreliably announced, and never appears on touch). `badge: true` means the
 *   label goes in a chip rather than as plain text.
 */
export function slaCell(ticket) {
  const due = formatDateTime(ticket?.sla_first_response_due_at);
  const met = formatDateTime(ticket?.sla_first_response_satisfied_at);

  switch (ticket?.sla_state) {
    case 'overdue':
      // The ONLY filled chip in this column, so exactly one thing shouts. This is
      // also where the breach flag now lives: it used to be a second chip inside
      // the SUBJECT cell, which cost 24px of row height on every breached row.
      return {
        label: 'Overdue',
        srLabel: null,
        className: 'badge badge-sla-overdue',
        badge: true,
        title: due ? `First reply was due ${due}` : 'First reply overdue',
      };

    case 'due_soon':
      // Text, not a pill: a filled amber chip is indistinguishable at a glance from
      // the STATUS column's amber "Open" badge two columns away, in both themes.
      return {
        label: 'Due soon',
        srLabel: null,
        className: 'sla-due-soon',
        badge: false,
        title: due ? `First reply due ${due}` : 'First reply due soon',
      };

    case 'on_track':
      return {
        label: 'On track',
        srLabel: null,
        className: MUTED,
        badge: false,
        title: due ? `First reply due ${due}` : 'First reply not yet due',
      };

    case 'met':
      // The majority cell on this desk: a phone ticket's clock is satisfied at the
      // moment it is created, because an agent was on the call. Its title therefore
      // shows the ticket's own creation time, which is correct — it is the same
      // fact TicketMeta.jsx was fixed to stop reporting as "First reply due
      // <tomorrow>" about a call that had just been answered on the spot.
      return {
        label: 'Met',
        srLabel: null,
        className: MUTED,
        badge: false,
        title: met ? `First reply met ${met}` : 'First reply met',
      };

    case 'closed':
      // Renders the same em-dash as `none` on purpose: for triage both mean "no
      // reply owed", and the distinction is not worth a second glyph. The enum
      // keeps them apart so this stays a pure map and the distinction survives if
      // it is ever wanted.
      return {
        label: null,
        srLabel: 'No reply owed',
        className: MUTED,
        badge: false,
        title: `No reply owed, this ticket is ${STATUS_LABELS[ticket?.status] ?? ticket?.status ?? 'resolved'}`,
      };

    default:
      // `none`, and anything the server sends that this build has never heard of.
      // Falling through to the em-dash rather than rendering the raw value is what
      // stops a future seventh state reaching an agent as the literal word
      // "undefined" in the column they are meant to triage on.
      return {
        label: null,
        srLabel: 'No first-reply SLA',
        className: MUTED,
        badge: false,
        title: 'No SLA policy applies to this ticket',
      };
  }
}
